import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm, useFormState } from 'react-hook-form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { JsonEditor } from '@library'
import { ActionsConfigTab } from '@mobileComponents/EventFlow'
import { MobileConfiguratorLayout } from '@mobileComponents/MobileConfiguratorLayout'
import { SaveDraftDialog } from '@mobileComponents/SaveDraftDialog'
import { TabPanel } from '@mobileComponents/TabPanel'
import { DEFAULT_MOBILE_APP } from '@mobileConstants'
import { exportToJSON, extractServerErrorMessage, validateMobileApp } from '@mobileHelpers'
import { useDraft, useMobileApps, useMobileScreens } from '@mobileHooks'
import { MobileAppsPreview, MobileDrawerPreview } from '@mobilePreviews'
import { useLazyFetchAssembledMobileAppConfigQuery } from '@mobileServices/mobileApp.api'
import { MobileApp } from '@mobileTypes'
import { Box, Button, CircularProgress, Paper, Tab, Tabs } from '@mui/material'

import { ROUTES } from '@constants'

import { BasicInfoForm } from './components/BasicInfoForm'
import { DrawerItemsTable } from './components/DrawerItemsTable'
import { PushNotificationsConfigTab } from './components/PushNotificationsConfigTab'
import { ScreensSelector } from './components/ScreensSelector'

export const MobileAppCreateOrEdit = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = !!id

  const { apps, createApp, updateApp, getAppById } = useMobileApps()
  const { screens } = useMobileScreens()
  const [fetchAssembledConfig] = useLazyFetchAssembledMobileAppConfigQuery()
  const { saveDraft, clearDraft, draft, hasDraft } = useDraft<MobileApp>(
    `mobile-app-draft-${id ?? 'new'}`
  )
  const shouldAutoRestore = useRef((location.state as any)?.restoreDraft === true)

  const [tabValue, setTabValue] = useState(0)
  const [jsonValue, setJsonValue] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [isExporting, setIsExporting] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const methods = useForm<MobileApp>({
    defaultValues: DEFAULT_MOBILE_APP,
  })

  const { control, handleSubmit, reset, watch } = methods
  const formData = watch()
  const { isDirty } = useFormState({ control })

  useEffect(() => {
    if (!isEdit && shouldAutoRestore.current && draft) {
      Object.keys(draft.data).forEach(key => {
        methods.setValue(key as any, (draft.data as any)[key], {
          shouldDirty: true,
          shouldValidate: false,
        })
      })
      setJsonValue(JSON.stringify(draft.data, null, 2))
      shouldAutoRestore.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEdit && id) {
      const app = getAppById(id)
      if (app) {
        if (shouldAutoRestore.current && draft) {
          Object.keys(draft.data).forEach(key => {
            methods.setValue(key as any, (draft.data as any)[key], {
              shouldDirty: true,
              shouldValidate: false,
            })
          })
          setJsonValue(JSON.stringify(draft.data, null, 2))
          shouldAutoRestore.current = false
        } else {
          reset(app)
          setJsonValue(JSON.stringify(app, null, 2))
        }
      }
    }
  }, [id, isEdit, getAppById, reset])

  useEffect(() => {
    if (tabValue === 4) {
      setJsonValue(JSON.stringify(formData, null, 2))
    }
  }, [tabValue])

  const onSubmit = async (data: MobileApp) => {
    const validationErrors = validateMobileApp(data, screens)
    if (validationErrors.length > 0) {
      setErrors(validationErrors)

      return
    }

    setErrors([])

    try {
      if (isEdit && id) {
        const updatedApp = await updateApp(id, data)
        reset(updatedApp)
        setJsonValue(JSON.stringify(updatedApp, null, 2))
        clearDraft()
      } else {
        const newApp = await createApp(data)
        clearDraft()
        navigate(`${ROUTES.CONFIG_MOBILE_APPS_EDIT}/${newApp.id}`, { replace: true })
      }
    } catch (error: unknown) {
      setSaveError(extractServerErrorMessage(error))
    }
  }

  const handleCancel = () => {
    navigate(ROUTES.CONFIG_MOBILE_APPS)
  }

  const handleReset = () => {
    if (isEdit && id) {
      const app = getAppById(id)
      if (app) {
        reset(app)
        setJsonValue(JSON.stringify(app, null, 2))
        setErrors([])
      }
    } else {
      reset(DEFAULT_MOBILE_APP)
      setJsonValue(JSON.stringify(DEFAULT_MOBILE_APP, null, 2))
      setErrors([])
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleExportFull = async () => {
    if (!id) return
    const app = getAppById(id)
    if (!app) return

    setIsExporting(true)
    try {
      const result = await fetchAssembledConfig(String(app.id)).unwrap()
      exportToJSON(result, `${app.title}_full_config.json`)
    } catch (error) {
      setErrors(['Failed to export config. Please try again.'])
    } finally {
      setIsExporting(false)
    }
  }

  const handleJsonChange = (newValue: string) => {
    try {
      const parsed = JSON.parse(newValue)
      // Use reset with keepDirtyValues: false to update form but keep it dirty
      Object.keys(parsed).forEach(key => {
        methods.setValue(key as any, parsed[key], { shouldDirty: true, shouldValidate: false })
      })
      setJsonValue(newValue)
      setErrors([])
    } catch (error) {
      setJsonValue(newValue)
    }
  }

  const previewComponent = useMemo(() => {
    // No preview for Screens tab (tab index 1) and JSON Editor (tab index 3)
    if (tabValue === 0) {
      return (
        <MobileAppsPreview
          apps={apps}
          currentApp={formData.title ? formData : null}
          highlightCurrentApp={!isEdit}
        />
      )
    } else if (tabValue === 2) {
      return <MobileDrawerPreview drawerItems={formData.drawerItems || []} />
    }

    return undefined
  }, [tabValue, apps, formData, isEdit])

  return (
    <>
      <SaveDraftDialog
        open={!!saveError}
        errorMessage={saveError ?? ''}
        onSaveDraft={() => saveDraft(methods.getValues())}
        onClose={() => setSaveError(null)}
      />
      <MobileConfiguratorLayout
        title={isEdit ? 'Edit Mobile App' : 'Create Mobile App'}
        isEdit={isEdit}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        onReset={handleReset}
        isDirty={isDirty}
        errors={errors}
        previewComponent={previewComponent}
        draftProps={{
          hasDraft,
          draftSavedAt: draft?.savedAt,
          onRestoreDraft: () => {
            if (draft) {
              reset(draft.data, { keepDefaultValues: true })
              setJsonValue(JSON.stringify(draft.data, null, 2))
            }
          },
          onDiscardDraft: clearDraft,
          onSaveDraft: () => saveDraft(methods.getValues()),
        }}
      >
        <Paper sx={{ width: '100%', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label='Basic Info' />
            <Tab label='Screens' />
            <Tab label='Drawer Items' />
            <Tab label='Actions' />
            <Tab label='Push Notifications' />
            <Tab label='JSON Editor' />
          </Tabs>
        </Paper>

        <FormProvider {...methods}>
          <TabPanel value={tabValue} index={0}>
            <BasicInfoForm isEdit={isEdit} />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <ScreensSelector
              selectedScreenIds={formData.screenIds || []}
              startScreen={formData.startScreen || ''}
              onChange={screenIds =>
                methods.setValue('screenIds', screenIds, { shouldDirty: true })
              }
              onStartScreenChange={startScreen =>
                methods.setValue('startScreen', startScreen, { shouldDirty: true })
              }
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <DrawerItemsTable />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <ActionsConfigTab />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <PushNotificationsConfigTab />
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <JsonEditor
              value={jsonValue}
              onChange={handleJsonChange}
              label='App Configuration (JSON)'
            />
            {isEdit && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant='outlined'
                  onClick={handleExportFull}
                  disabled={isExporting}
                  startIcon={isExporting ? <CircularProgress size={20} /> : null}
                >
                  {isExporting ? 'Exporting...' : 'Export Full Config'}
                </Button>
              </Box>
            )}
          </TabPanel>
        </FormProvider>
      </MobileConfiguratorLayout>
    </>
  )
}
